import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { EyeIcon, TrashIcon, PlusIcon, ArrowTrendingUpIcon, FunnelIcon, ChevronDownIcon, ArrowPathIcon, KeyIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarFilledIcon } from '@heroicons/react/24/solid';
import { StarIcon } from '@heroicons/react/24/outline';
import PageBanner from '../../components/utils/PageBanner';
import CreateCredentialModal from './CreateCredentialModal';
import CredentialDetailsModal from './CredentialDetailsModal';
import IncreaseQuotaModal from './IncreaseQuotaModal';
import DeleteCredentialModal from './DeleteCredentialModal';
import RotateCredentialModal from './RotateCredentialModal';
import GpgKeyModal from './GpgKeyModal';
import HelpModal from './HelpModal';
import CredentialsTutorial from './CredentialsTutorial';
import GraphqlService from '../../service/graphqlService';
import { useSnackbar } from '../../providers/SnackbarContext';
import { useAuditLog, AUDIT_ENTITY_CERTIFICATE_PROVISIONING, AUDIT_CHANGE_CREDENTIAL_CREATION, AUDIT_CHANGE_FIELD_UPDATE, AUDIT_CHANGE_QUOTA_INCREASE } from '../../hooks/useAuditLog';

import { UPDATE_LOGICAL_USER, UPDATE_CERTIFICATE_QUOTA, DELETE_PHYSICAL_USER, ROTATE_CREDENTIAL } from '../../utils/constants';

import usePermission from '../../authorization/permissionHook';
import useAnalytics from '../../hooks/useAnalytics';
import { shouldShowIpWhitelist } from '../../utils/environmentConfig';
import { EXPIRATION_STATUS, getExpirationInfo, getExpirationPillStyle } from '../../utils/credentialExpiration';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';

// Placeholder for LoadingSpinner if not available
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-32">
    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
    <p className="ml-2 text-gray-700">Loading data...</p>
  </div>
);

// Helper function for styling the quota pill
const getQuotaPillStyle = (remainingQuota, alertThreshold) => {
  let bgColor = 'bg-green-100';
  let textColor = 'text-green-700';
  let borderColor = 'border-green-400';

  const currentQuota = Number(remainingQuota) || 0;
  const threshold = Number(alertThreshold) || 0;

  if (currentQuota <= threshold) {
    bgColor = 'bg-red-100';
    textColor = 'text-red-700';
    borderColor = 'border-red-400';
  } else if (currentQuota <= threshold * 1.5) {
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-700';
    borderColor = 'border-yellow-400';
  }
  return `px-3 py-1 text-sm font-medium rounded-full inline-block border ${bgColor} ${textColor} ${borderColor}`;
};

const getCredentialDescription = (description) => {
  return description || 'N/A';
};

const getQuotaData = (quotaData) => {
  const quota = quotaData?.quota || 0;
  const threshold = quotaData?.threshold || 0;
  return { quota, threshold };
};

export default function CredentialsManagement() {
  const [filteredCredentials, setFilteredCredentials] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [showIncreaseQuotaModal, setShowIncreaseQuotaModal] = useState(false);
  const [credentialForQuotaIncrease, setCredentialForQuotaIncrease] = useState(null);
  const [showPermanentDeleteModal, setShowPermanentDeleteModal] = useState(false);
  const [credentialToPermanentlyDelete, setCredentialToPermanentlyDelete] = useState(null);
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [credentialToRotate, setCredentialToRotate] = useState(null);
  // Feature 032 - GPG key update modal
  const [showGpgModal, setShowGpgModal] = useState(false);
  const [credentialForGpg, setCredentialForGpg] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [projectsToView, setProjectsToView] = useState([]);
  const [favoriteCredentialIds, setFavoriteCredentialIds] = useState([]);

  // Load favorite credentials from localStorage on mount
  useEffect(() => {
    const storedFavorites = localStorage.getItem('favoriteCredentials');
    if (storedFavorites) {
      try {
        setFavoriteCredentialIds(JSON.parse(storedFavorites));
      } catch (e) {
        console.error("Failed to parse favorite credentials from localStorage", e);
        setFavoriteCredentialIds([]);
      }
    }
  }, []);

  // Save favorite credentials to localStorage when they change
  useEffect(() => {
    localStorage.setItem('favoriteCredentials', JSON.stringify(favoriteCredentialIds));
  }, [favoriteCredentialIds]);

  // Add permission checks
  const [, canUpdateUser] = usePermission(UPDATE_LOGICAL_USER);
  const [, canDeleteUser] = usePermission(DELETE_PHYSICAL_USER);
  const [, canUpdateQuota] = usePermission(UPDATE_CERTIFICATE_QUOTA);
  const [, canRotateCredential] = usePermission(ROTATE_CREDENTIAL);

  const { setAnalytics } = useAnalytics();

  useEffect(() => {
    setAnalytics({
      event: 'section_loaded',
      sectionId: 'certificate_management-provisioning',
    });
  }, [setAnalytics]);

  const [filters, setFilters] = useState({
    userId: '',
    description: '',
    quota: '',
    threshold: '',
    pointOfContact: '',
    project: [],
    expirationStatus: '',
  });

  const [showFilters, setShowFilters] = useState(false);

  // Feature 030 - sort the list by days-until-expiry (most urgent first) when the
  // Expiration column header is clicked; null means no explicit expiration sort applied.
  const [expirationSortDirection, setExpirationSortDirection] = useState(null);

  const toggleExpirationSort = () => {
    setExpirationSortDirection((prev) => {
      if (prev === 'asc') return 'desc';
      if (prev === 'desc') return null;
      return 'asc';
    });
  };

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const {
    data: credentials = [],
    isLoading: loading,
  } = useQuery({
    queryKey: ['physicalUsers'],
    queryFn: GraphqlService.getPhysicalUsers,
    select: (data) => {
      const result = data?.filter(user => user.quotaData) // Only include users with quotaData
        .map(user => ({
          id: user.id,
          userId: user.userId,
          logicalUser: user.logicalUser || 'N/A',
          description: user.description || 'N/A',
          quotaData: user.quotaData,
          accessKeys: user.accessKeys || [],
          logicalUserData: user.logicalUserData || { pointOfContact: 'N/A' },
          expirationDate: user.expirationDate || null,
          latestRotation: user.latestRotation || null,
        })) || [];
      // delete duplicate, same id
      const uniqueCredentials = Array.from(new Map(result.map(item => [item.id, item])).values());
      return uniqueCredentials;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(filter => {
      if (Array.isArray(filter)) return filter.length > 0;
      return filter !== '';
    });

    // Feature 030 - optional sort by days-until-expiry (most urgent first when 'asc').
    const applyExpirationSort = (list) => {
      if (!expirationSortDirection) return list;
      return [...list].sort((a, b) => {
        const aDays = getExpirationInfo(a.expirationDate).daysUntilExpiry;
        const bDays = getExpirationInfo(b.expirationDate).daysUntilExpiry;
        // Credentials with no expiry date sort last regardless of direction.
        if (aDays === null && bDays === null) return 0;
        if (aDays === null) return 1;
        if (bDays === null) return -1;
        return expirationSortDirection === 'asc' ? aDays - bDays : bDays - aDays;
      });
    };

    if (hasActiveFilters) {
      const filtered = credentials.filter((credential) => {
        const userId = credential.userId?.toLowerCase() || '';
        const description = getCredentialDescription(credential.description)?.toLowerCase() || '';
        const pointOfContact = credential.logicalUserData?.pointOfContact?.toLowerCase() || '';
        const { quota, threshold } = getQuotaData(credential.quotaData);

        const credentialProjects = credential.logicalUserData?.projects?.map(p => p.code) || [];

        const hasProject = filters.project.length === 0 || filters.project.some(filterProject => credentialProjects.includes(parseInt(filterProject)));

        // Normalize quota and threshold by removing commas and periods for comparison
        const normalizeNumber = (num) => num.toString().replace(/[,.]/g, '');
        const normalizedQuota = normalizeNumber(quota);
        const normalizedThreshold = normalizeNumber(threshold);
        const normalizedFilterQuota = filters.quota.replace(/[,.]/g, '');
        const normalizedFilterThreshold = filters.threshold.replace(/[,.]/g, '');

        const { status: expirationStatus } = getExpirationInfo(credential.expirationDate);
        const hasExpirationStatus = !filters.expirationStatus || expirationStatus === filters.expirationStatus;

        return (
          (!filters.userId || userId.includes(filters.userId.toLowerCase())) &&
          (!filters.description || description.includes(filters.description.toLowerCase())) &&
          (!filters.quota || normalizedQuota === normalizedFilterQuota) &&
          (!filters.threshold || normalizedThreshold === normalizedFilterThreshold) &&
          (!filters.pointOfContact || pointOfContact.includes(filters.pointOfContact.toLowerCase())) &&
          hasProject &&
          hasExpirationStatus
        );
      });
      setFilteredCredentials(applyExpirationSort(filtered));
      return;
    }

    // Sort credentials to put favorites at the top
    const sortedCredentials = [...credentials].sort((a, b) => {
      const aIsFavorite = favoriteCredentialIds.includes(a.id);
      const bIsFavorite = favoriteCredentialIds.includes(b.id);

      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });

    setFilteredCredentials(applyExpirationSort(sortedCredentials));

  }, [filters, credentials, favoriteCredentialIds, expirationSortDirection]);


  const openSnackBar = useSnackbar();
  const queryClient = useQueryClient();

  const [availableProjects, setAvailableProjects] = useState([]);

  const fetchProjects = useCallback(async () => GraphqlService.getProjects(), []);
  const {
    data: projectsLayoutData,
    isLoading: projectsLoading,
    error: projectsError
  } = useQuery(['projectsLayout'], fetchProjects, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (projectsLayoutData) {
      const formattedProjects = projectsLayoutData.map(project => ({
        value: project.code,
        label: project.name,
      }));
      setAvailableProjects(formattedProjects);
    } else {
      setAvailableProjects([]);
    }
  }, [projectsLayoutData]);

  // Use the audit log hook
  const { logAuditChange: logAudit } = useAuditLog();

  // Wrapper function to maintain compatibility with existing code
  const logAuditChange = (changeType, entityId, detail = null, value = null) => {
    return logAudit(AUDIT_ENTITY_CERTIFICATE_PROVISIONING, changeType, entityId, detail, value);
  };

  const handleCreateNew = () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-add' }
    });
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCredential(null);
  };

  // Feature 034/026 - the guided tutorial walks through the details modal too
  // (Access Keys, Enable, Permissions). It asks us to open/close the real
  // modal for the first credential in the list as it enters/leaves those steps.
  const [tutorialOpenedDetails, setTutorialOpenedDetails] = useState(false);
  const handleTutorialRequestDetailsModal = (shouldOpen) => {
    if (shouldOpen) {
      if (filteredCredentials.length === 0) return;
      setSelectedCredential(filteredCredentials[0]);
      setShowDetailsModal(true);
      setTutorialOpenedDetails(true);
    } else if (tutorialOpenedDetails) {
      setShowDetailsModal(false);
      setSelectedCredential(null);
      setTutorialOpenedDetails(false);
    }
  };

  const handleOpenIncreaseQuotaModal = (credential) => {
    // Only proceed if user has permission to update certificate quota
    if (canUpdateQuota) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-increase_quota' }
      });
      setCredentialForQuotaIncrease(credential);
      setShowIncreaseQuotaModal(true);
    } else {
      openSnackBar('You do not have permission to increase certificate quota.', 'error');
    }
  };

  const handleCloseIncreaseQuotaModal = () => {
    setShowIncreaseQuotaModal(false);
    setCredentialForQuotaIncrease(null);
  };

  const handleConfirmIncreaseQuota = async (id, amountToIncrease, result) => {
    // Check permission first
    if (!canUpdateQuota) {
      openSnackBar('You do not have permission to increase certificate quota.', 'error');
      handleCloseIncreaseQuotaModal();
      return;
    }

    // Check if we have audit info from the modal
    if (result && result.success && result.auditInfo) {
      // Log the audit using the provided info
      logAudit(
        result.auditInfo.entity,
        result.auditInfo.change,
        result.auditInfo.entityId,
        result.auditInfo.description,
        result.auditInfo.value
      );
      openSnackBar('Quota increased successfully!', 'success');
      // Refresh the list of credentials from the server
      queryClient.invalidateQueries(['physicalUsers']);
    } else {
      // Fallback to the old method if no audit info is provided
      const credentialToUpdate = credentials.find(cred => cred.id === id);
      if (credentialToUpdate) {
        const increaseAmount = parseInt(String(amountToIncrease), 10);
        if (!isNaN(increaseAmount) && increaseAmount > 0) {
          try {
            await GraphqlService.updateCertificateQuota(credentialToUpdate.userId, increaseAmount, credentialToUpdate.quotaData.threshold);
            logAuditChange(AUDIT_CHANGE_QUOTA_INCREASE, id, 'Remaining Quota', increaseAmount);
            openSnackBar('Quota increased successfully!', 'success');
            // Refresh the list of credentials from the server
            queryClient.invalidateQueries(['physicalUsers']);
          } catch (error) {
            openSnackBar('Failed to increase quota. Please try again.', 'error');
          }
        }
      }
    }
    handleCloseIncreaseQuotaModal();
  };

  const handleAddCredential = (data) => {
    // Close the modal
    setShowCreateModal(false);

    // Refresh the list of credentials from the server
    queryClient.invalidateQueries(['physicalUsers']);

    // If we have data from the mutation response
    if (data) {
      // Get credential ID from response or use name as fallback
      const newCredentialId = data.id || data.userId || data.name || '';

      console.log('Creating audit log for new credential:', newCredentialId, data);

      // For new credentials, only create a single audit log entry for the creation event
      // This prevents duplicate logs for each field that was set during creation
      if (data.isNewCredential) {
        // Create a comprehensive description of the new credential
        const creationDetails = `Created new credential with quota: ${data.quota}, threshold: ${data.alertThreshold}, description: ${data.description || 'N/A'}`;

        // Log a single comprehensive audit change for credential creation
        logAuditChange(AUDIT_CHANGE_CREDENTIAL_CREATION, newCredentialId, 'New Credential', creationDetails);
      } else {
        // This is for backward compatibility or if called from elsewhere without the isNewCredential flag
        // Log audit change for credential creation - this is the main entry
        logAuditChange(AUDIT_CHANGE_CREDENTIAL_CREATION, newCredentialId, 'Credential', data.name || 'New Credential');

        // Log additional audit entries for important credential properties
        if (data.description) {
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'Description', data.description);
        }

        if (data.quota || data.quotaData?.quota) {
          const quota = data.quota || data.quotaData?.quota;
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'Quota', quota);
        }

        if (data.alertThreshold || data.quotaData?.threshold) {
          const threshold = data.alertThreshold || data.quotaData?.threshold;
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'Alert Threshold', threshold);
        }

        if (data.pointOfContact || data.logicalUserData?.pointOfContact) {
          const pointOfContact = data.pointOfContact || data.logicalUserData?.pointOfContact;
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'Point of Contact', pointOfContact);
        }
      }

      // Only log these field updates if not a new credential (already included in creation log)
      if (!data.isNewCredential) {
        if (shouldShowIpWhitelist() && data.iPWhiteList) {
          const ipList = Array.isArray(data.iPWhiteList)
            ? data.iPWhiteList.join(', ')
            : data.iPWhiteList;
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'IP Whitelist', ipList);
        }

        if (data.projects && data.projects.length > 0) {
          const projectCodes = data.projects.join(', ');
          logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, newCredentialId, 'Projects', projectCodes);
        }
      }

      // Notify user about successful audit logging
      openSnackBar('Credential created and audit logs generated successfully', 'success');
    } else {
      // Fallback if no data is provided - log a generic entry
      logAuditChange(AUDIT_CHANGE_CREDENTIAL_CREATION, 'new-credential', 'Credential', 'New credential created');
      openSnackBar('Credential created, but limited audit information available', 'info');
    }
  };

  const handleViewDetails = (id) => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-info' }
    });
    const credentialToView = credentials.find(cred => cred.id === id);
    if (credentialToView) {
      setSelectedCredential(credentialToView);
      setShowDetailsModal(true);
    }
  };

  const handleUpdateCredential = (id, updatedData) => {
    // Now we only receive fields that have actually changed
    // updatedData contains only the changed fields plus id and originalCredential

    // Log each changed field separately
    if (updatedData.alertThreshold !== undefined) {
      logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, id, 'Alert Threshold', updatedData.alertThreshold);
    }

    if (shouldShowIpWhitelist() && updatedData.iPWhiteList !== undefined) {
      const ipList = Array.isArray(updatedData.iPWhiteList)
        ? updatedData.iPWhiteList.join(', ')
        : updatedData.iPWhiteList;
      logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, id, 'Whitelisted IPs', ipList);
    }

    if (updatedData.pointOfContact !== undefined) {
      logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, id, 'Points of Contact', updatedData.pointOfContact);
    }

    if (updatedData.description !== undefined) {
      logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, id, 'Description', updatedData.description);
    }

    if (updatedData.brands !== undefined) {
      logAuditChange(AUDIT_CHANGE_FIELD_UPDATE, id, 'Products', updatedData.brands.join(', '));
    }

    // Update the table in CertificateProvisioning
    queryClient.invalidateQueries(['physicalUsers']);

    queryClient.invalidateQueries(['auditLogs', String(id)]);
    openSnackBar(`Credential updated successfully!`, 'success');
  };

  const handlePermanentDeleteCredential = (credential) => {
    // Only proceed if user has permission to delete credentials
    if (canDeleteUser) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-delete' }
      });
      setCredentialToPermanentlyDelete(credential);
      setShowPermanentDeleteModal(true);
    } else {
      openSnackBar('You do not have permission to permanently delete credentials.', 'error');
    }
  };

  const handleOpenRotateModal = (credential) => {
    if (canRotateCredential) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-rotate' }
      });
      setCredentialToRotate(credential);
      setShowRotateModal(true);
    } else {
      openSnackBar('You do not have permission to rotate credentials.', 'error');
    }
  };

  const handleCloseRotateModal = () => {
    setShowRotateModal(false);
    setCredentialToRotate(null);
  };
  const handleRotateSuccess = (result) => {
    if (result && result.success) {
      openSnackBar('Credential rotated successfully!', 'success');
      const rotatedId = result.credential?.id;
      queryClient.invalidateQueries(['physicalUsers']);
      if (rotatedId) {
        queryClient.invalidateQueries(['auditLogs', String(rotatedId)]);
      }
    }
  };

  // Feature 032 - open the GPG key modal for a credential's logical user
  const handleOpenGpgModal = (credential) => {
    if (canUpdateUser) {
      setAnalytics({
        event: 'button_clicked',
        extraParams: { name: 'certificate_management-provisioning-gpg_key' },
      });
      setCredentialForGpg(credential);
      setShowGpgModal(true);
    } else {
      openSnackBar('You do not have permission to update the GPG key.', 'error');
    }
  };

  const handleCloseGpgModal = () => {
    setShowGpgModal(false);
    setCredentialForGpg(null);
  };

  const handleGpgSuccess = (result) => {
    if (result && result.rotated) {
      queryClient.invalidateQueries(['physicalUsers']);
    }
  };

  const confirmPermanentDeleteCredential = (result) => {
    if (result && result.success) {
      // Use the audit info from the modal if available
      if (result.auditInfo) {
        logAudit(
          result.auditInfo.entity,
          result.auditInfo.change,
          result.auditInfo.entityId,
          result.auditInfo.description,
          result.auditInfo.value
        );
      }

      const credentialName = result.credential?.logicalUser || credentialToPermanentlyDelete?.logicalUser || 'Credential';
      openSnackBar(`Credential "${credentialName}" permanently deleted!`, 'success');
    } else if (result && !result.success) {
      openSnackBar(`Error deleting credential: ${result.error || 'Unknown error'}`, 'error');
    }

    setShowPermanentDeleteModal(false);
    setCredentialToPermanentlyDelete(null);

    // Refresh the credentials list - use refetchQueries to force immediate refetch
    queryClient.refetchQueries({ queryKey: ['physicalUsers'] });
  };

  const handleClearFilters = () => {
    setFilters({
      userId: '',
      description: '',
      quota: '',
      threshold: '',
      pointOfContact: '',
      project: [],
      expirationStatus: '',
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'project') {
      // This will be handled by the new component callback
      return;
    }
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleProjectFilterChange = (selectedProjects) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      project: selectedProjects,
    }));
  };

  const handleViewAllProjects = (projects) => {
    setProjectsToView(projects);
    setShowProjectsModal(true);
  };

  const handleCloseProjectsModal = () => {
    setShowProjectsModal(false);
    setProjectsToView([]);
  };

  const toggleFavoriteCredential = (credentialId, event) => {
    if (event) {
      event.stopPropagation(); // Prevent triggering row actions
    }

    setFavoriteCredentialIds(prevFavoriteIds => {
      if (prevFavoriteIds.includes(credentialId)) {
        return prevFavoriteIds.filter(id => id !== credentialId);
      }
      return [...prevFavoriteIds, credentialId];
    });
  };

  return (
    <div id="quota-management" className="bg-gray-100 min-h-screen">
      <PageBanner title="CREDENTIALS MANAGEMENT" subtitle="Handle your Project's Credentials and their Certificate's Quota." />

      <div className="p-6">
        <div className="bg-white shadow-md rounded-lg">

          <div className="p-4 flex justify-between items-center border-b border-gray-200">
            <button
              onClick={handleCreateNew}
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
              title="Add New Credential"
              data-tutorial="add-credential"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add New Credential
            </button>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowTutorial(true)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 w-9 h-9 rounded-full flex items-center justify-center shadow-md font-bold"
                title="Help - Guided Tutorial"
                aria-label="Help"
              >
                ?
              </button>
              {!showFilters ? (
                <button
                  onClick={toggleFilters}
                  className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full flex items-center shadow-md"
                  title="Show Filters"
                  data-tutorial="filters"
                >
                  <FunnelIcon className="h-5 w-5" />
                </button>
              ) : (
                <>
                  <button
                    onClick={toggleFilters}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full flex items-center shadow-md transition-transform"
                    title="Hide Filters"
                  >
                    <ChevronDownIcon className="h-5 w-5 rotate-180" />
                  </button>
                  <button
                    onClick={handleClearFilters}
                    className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-full flex items-center shadow-md"
                    title="Clear Filters"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {loading ?
            <LoadingSpinner /> :


            <div className="hidden md:flex bg-blue-600 text-white items-center">
              <div className="flex items-center flex-grow">
                <div className="p-4 w-2/12 font-semibold truncate">Description</div>
                <div className="p-4 w-2/12 font-semibold truncate">Product</div>
                <div className="p-4 w-1/12 font-semibold text-center">Quota</div>
                <div className="p-4 w-1/12 font-semibold text-center">Threshold</div>
                <div className="p-4 w-2/12 font-semibold">Point of Contact</div>
                <button
                  type="button"
                  onClick={toggleExpirationSort}
                  className="p-4 w-2/12 font-semibold text-center flex items-center justify-center gap-1 hover:bg-blue-700"
                  title="Sort by expiration (most urgent first)"
                >
                  Expiration
                  {expirationSortDirection && (
                    <ChevronDownIcon className={`h-4 w-4 ${expirationSortDirection === 'desc' ? 'rotate-180' : ''}`} />
                  )}
                </button>
                <div className="p-4 w-2/12 font-semibold text-center">Actions</div>
              </div>
            </div>
          }

          {showFilters && (
            <div className="hidden md:flex bg-gray-100 border-b border-gray-200">
              <div className="flex items-center flex-grow">
                <div className="p-4 w-3/12">
                  <input
                    type="text"
                    name="description"
                    value={filters.description}
                    onChange={handleFilterChange}
                    placeholder="Filter by Description"
                    className="border rounded p-2 w-full text-sm"
                  />
                </div>
                <div className="p-4 w-2/12">
                  <MultiSelectDropdown
                    options={availableProjects}
                    selectedValues={filters.project}
                    onChange={handleProjectFilterChange}
                    placeholder="Select Products"
                  />
                </div>
                <div className="p-4 w-1/12">
                  <input
                    type="text"
                    name="quota"
                    value={filters.quota}
                    onChange={handleFilterChange}
                    placeholder="Filter by Quota"
                    className="border rounded p-2 w-full text-sm text-center"
                  />
                </div>
                <div className="p-4 w-1/12">
                  <input
                    type="text"
                    name="threshold"
                    value={filters.threshold}
                    onChange={handleFilterChange}
                    placeholder="Filter by Threshold"
                    className="border rounded p-2 w-full text-sm text-center"
                  />
                </div>
                <div className="p-4 w-2/12">
                  <input
                    type="text"
                    name="pointOfContact"
                    value={filters.pointOfContact}
                    onChange={handleFilterChange}
                    placeholder="Filter by Point of Contact"
                    className="border rounded p-2 w-full text-sm"
                  />
                </div>
                <div className="p-4 w-2/12">
                  <select
                    name="expirationStatus"
                    value={filters.expirationStatus}
                    onChange={handleFilterChange}
                    className="border rounded p-2 w-full text-sm"
                  >
                    <option value="">All Expirations</option>
                    <option value={EXPIRATION_STATUS.OK}>OK</option>
                    <option value={EXPIRATION_STATUS.EXPIRING_SOON}>Expiring soon</option>
                    <option value={EXPIRATION_STATUS.EXPIRED}>Expired</option>
                    <option value={EXPIRATION_STATUS.NO_EXPIRY}>No expiry</option>
                  </select>
                </div>
                <div className="p-4 w-1/12">
                  {/* Empty space for Actions column */}
                </div>
              </div>
            </div>
          )}

          {showFilters && (
            <div className="md:hidden grid grid-cols-1 gap-4 p-4 bg-gray-100">
              <input
                type="text"
                name="description"
                value={filters.description}
                onChange={handleFilterChange}
                placeholder="Filter by Description"
                className="border rounded p-2 w-full"
              />
              <MultiSelectDropdown
                options={availableProjects}
                selectedValues={filters.project}
                onChange={handleProjectFilterChange}
                placeholder="Select Products"
              />
              <input
                type="text"
                name="quota"
                value={filters.quota}
                onChange={handleFilterChange}
                placeholder="Filter by Quota"
                className="border rounded p-2 w-full"
              />
              <input
                type="text"
                name="threshold"
                value={filters.threshold}
                onChange={handleFilterChange}
                placeholder="Filter by Threshold"
                className="border rounded p-2 w-full"
              />
              <input
                type="text"
                name="pointOfContact"
                value={filters.pointOfContact}
                onChange={handleFilterChange}
                placeholder="Filter by Point of Contact"
                className="border rounded p-2 w-full"
              />
              <select
                name="expirationStatus"
                value={filters.expirationStatus}
                onChange={handleFilterChange}
                className="border rounded p-2 w-full"
              >
                <option value="">All Expirations</option>
                <option value={EXPIRATION_STATUS.OK}>OK</option>
                <option value={EXPIRATION_STATUS.EXPIRING_SOON}>Expiring soon</option>
                <option value={EXPIRATION_STATUS.EXPIRED}>Expired</option>
                <option value={EXPIRATION_STATUS.NO_EXPIRY}>No expiry</option>
              </select>
            </div>
          )}

          {filteredCredentials.length !== 0 && (
            <div>
              {(() => {
                let separatorRendered = false;
                const favoriteCount = filteredCredentials.filter(cred => favoriteCredentialIds.includes(cred.id)).length;

                return filteredCredentials.map((item, index) => {
                  const { quota, threshold } = getQuotaData(item.quotaData);
                  const id = item.id || item.name || 'N/A';
                  const isFavorite = favoriteCredentialIds.includes(item.id);

                  // Add separator between favorites and non-favorites
                  let separator = null;
                  if (!isFavorite && !separatorRendered && favoriteCount > 0 && index >= favoriteCount) {
                    separator = <div key="separator" className="border-b-2 border-gray-300 my-2" />;
                    separatorRendered = true;
                  }

                  return (
                    <React.Fragment key={id}>
                      {separator}

                      <div key={id} className="flex flex-col md:flex-row border-b border-gray-200 hover:bg-gray-50 items-start">
                        <div className="p-4 w-2/12 truncate flex items-center">
                          <button
                            onClick={(e) => toggleFavoriteCredential(item.id, e)}
                            className="mr-2 focus:outline-none"
                            title={favoriteCredentialIds.includes(item.id) ? "Remove from favorites" : "Add to favorites"}
                            aria-label={favoriteCredentialIds.includes(item.id) ? "Remove from favorites" : "Add to favorites"}
                            data-tutorial={index === 0 ? "favorite" : undefined}
                          >
                            {favoriteCredentialIds.includes(item.id) ? (
                              <StarFilledIcon className="h-5 w-5 text-yellow-500" />
                            ) : (
                              <StarIcon className="h-5 w-5 text-gray-400 hover:text-yellow-500" />
                            )}
                          </button>
                          {getCredentialDescription(item.description)}
                        </div>
                        <div className="p-4 w-2/12">
                          {item.logicalUserData?.projects && item.logicalUserData?.projects.length > 0 ? (
                            <div className="flex flex-col">
                              <div className="max-h-20 overflow-y-auto">
                                {item.logicalUserData?.projects.filter((project) => project.code || project.name)
                                  .map((project) => (
                                    <span key={project.code || project.name} className="inline-block bg-blue-100 text-blue-800 rounded-full px-2 py-1 text-xs font-semibold mr-2 mb-2">
                                      {project.name || project.code}
                                    </span>
                                  ))}
                              </div>
                              {item.logicalUserData?.projects.filter((project) => project.code || project.name).length > 3 && (
                                <button
                                  onClick={() => handleViewAllProjects(item.logicalUserData.projects)}
                                  className="text-blue-500 hover:underline text-xs mt-2 self-start"
                                >
                                  View All
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500">No Projects</span>
                          )}
                        </div>
                        <div className="p-4 w-1/12 text-center">
                          <span className={getQuotaPillStyle(quota, threshold)}>
                            {quota.toLocaleString()}
                          </span>
                        </div>
                        <div className="p-4 w-1/12 text-center">{threshold}</div>
                        <div className="p-4 w-2/12 break-words">{item.logicalUserData?.pointOfContact || 'N/A'}</div>
                        <div className="p-4 w-2/12 text-center" data-tutorial={index === 0 ? "expiration-status" : undefined}>
                          {(() => {
                            const { status, daysUntilExpiry } = getExpirationInfo(item.expirationDate);
                            return (
                              <span
                                className={getExpirationPillStyle(status)}
                                title={item.expirationDate ? new Date(item.expirationDate).toLocaleDateString() : 'No expiration date set'}
                              >
                                {status}
                                {status === EXPIRATION_STATUS.EXPIRING_SOON && daysUntilExpiry !== null && ` (${daysUntilExpiry}d)`}
                              </span>
                            );
                          })()}
                        </div>
                        <div className="p-2 w-2/12 flex flex-nowrap justify-center items-center space-x-1">
                          <button
                            onClick={() => handleViewDetails(item.id)}
                            title={canUpdateUser ? "View & Edit Details" : "View Details (Read-only)"}
                            className="text-gray-500 hover:text-blue-600"
                            data-tutorial={index === 0 ? "view-details" : undefined}
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => canUpdateQuota && handleOpenIncreaseQuotaModal(item)}
                            title={canUpdateQuota ? "Increase Quota" : "Cannot Increase Quota (No Permission)"}
                            className={`text-gray-500 ${canUpdateQuota ? "hover:text-green-600 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                            data-tutorial={index === 0 ? "increase-quota" : undefined}
                          >
                            <ArrowTrendingUpIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => canRotateCredential && handleOpenRotateModal(item)}
                            title={canRotateCredential ? "Rotate Credentials" : "Cannot Rotate (No Permission)"}
                            className={`text-gray-500 ${canRotateCredential ? "hover:text-indigo-600 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                            data-tutorial={index === 0 ? "rotate-credentials" : undefined}
                          >
                            <ArrowPathIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => canUpdateUser && handleOpenGpgModal(item)}
                            title={canUpdateUser ? "Update GPG Key" : "Cannot Update GPG Key (No Permission)"}
                            className={`text-gray-500 ${canUpdateUser ? "hover:text-indigo-600 cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                            data-tutorial={index === 0 ? "gpg-key" : undefined}
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => canDeleteUser && handlePermanentDeleteCredential(item)}
                            title={canDeleteUser ? "Permanently Delete Credential" : "Cannot Delete (No Permission)"}
                            className={`${canDeleteUser ? "text-red-500 hover:text-red-700 cursor-pointer" : "text-gray-400 opacity-50 cursor-not-allowed"}`}
                            data-tutorial={index === 0 ? "delete-credential" : undefined}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })
              })()}
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateCredentialModal
          isOpen={showCreateModal}
          onClose={handleCloseModal}
          onAdd={handleAddCredential}
          availableProjects={availableProjects}
          projectsLoading={projectsLoading}
          projectsError={projectsError}
        />
      )}

      {showDetailsModal && selectedCredential && (
        <CredentialDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          credential={selectedCredential}
          onUpdate={handleUpdateCredential}
          projects={availableProjects}
          canEdit={canUpdateUser}
        />
      )}

      {showIncreaseQuotaModal && credentialForQuotaIncrease && canUpdateQuota && (
        <IncreaseQuotaModal
          isOpen={showIncreaseQuotaModal}
          onClose={handleCloseIncreaseQuotaModal}
          credential={credentialForQuotaIncrease}
          onIncrease={handleConfirmIncreaseQuota}
        />
      )}

      {showPermanentDeleteModal && credentialToPermanentlyDelete && canDeleteUser && (
        <DeleteCredentialModal
          isOpen={showPermanentDeleteModal}
          credential={credentialToPermanentlyDelete}
          onClose={() => {
            setShowPermanentDeleteModal(false);
            setCredentialToPermanentlyDelete(null);
          }}
          onSuccess={confirmPermanentDeleteCredential}
        />
      )}

      {showRotateModal && credentialToRotate && canRotateCredential && (
        <RotateCredentialModal
          isOpen={showRotateModal}
          credential={credentialToRotate}
          onClose={handleCloseRotateModal}
          onSuccess={handleRotateSuccess}
        />
      )}

      {showGpgModal && credentialForGpg && canUpdateUser && (
        <GpgKeyModal
          isOpen={showGpgModal}
          credential={credentialForGpg}
          linkedCredentials={credentials.filter((c) => c.logicalUser === credentialForGpg.logicalUser)}
          canRotate={canRotateCredential}
          onClose={handleCloseGpgModal}
          onSuccess={handleGpgSuccess}
        />
      )}

      {showHelpModal && (
        <HelpModal
          isOpen={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          onStartTutorial={() => {
            setShowHelpModal(false);
            setShowTutorial(true);
          }}
        />
      )}

      {showTutorial && (
        <CredentialsTutorial
          isOpen={showTutorial}
          onClose={() => {
            setShowTutorial(false);
            handleTutorialRequestDetailsModal(false);
          }}
          hasCredentials={filteredCredentials.length > 0}
          onRequestDetailsModal={handleTutorialRequestDetailsModal}
          onShowWrittenGuide={() => {
            setShowTutorial(false);
            handleTutorialRequestDetailsModal(false);
            setShowHelpModal(true);
          }}
        />
      )}

      {showProjectsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-md mx-4">
            <h2 className="text-2xl font-semibold mb-6 text-gray-900">All Projects</h2>
            <div className="max-h-96 overflow-y-auto">
              <div className="flex flex-wrap gap-3">
                {projectsToView.filter(project => project.code || project.name).map((project) => (
                  <span
                    key={project.code || project.name}
                    className="inline-block bg-blue-100 text-blue-800 rounded-full px-4 py-2 text-sm font-semibold border border-blue-200"
                  >
                    {project.name || project.code}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={handleCloseProjectsModal}
              className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}